import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { formatDate, sortTable } from '../../../../../../../common/SharedFunctions';
import { BrokerClientApi } from '@apply/api';
import { applicationInsights } from '../../../../../../../ApplicationInsights';
import { RefreshEvents } from '../../../../../../../common/SharedEvents';
import {
    Card,
    CardHeaderTheme,
    CardBorderRadius,
    CardBorderStyle,
    CardTheme,
    Table,
    TableHeaderTheme,
    TableBorderRadius,
    TableBorderStyle,
    TableRowTheme,
    TableColumn,
    TableRow,
} from '@apply/components';
import { RepresentativeCell } from './components/RepresentativeCell';
import { BusinessNameCell } from './components/BusinessNameCell';
import { ICCAStartDateCell } from './components/ICCAStartDateCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/free-regular-svg-icons';
import { TableFilteringOperators } from './constants/TableFilteringOperators';
import { SortState } from './constants/TableSortingOperators';
import { ICCACell } from './components/ICCACell';
import { EmptyReferrals } from './components/EmptyReferrals';
import { ReferralTableLoadIndicator } from './components/ReferralTableLoadIndicator';
import { handleAdminInfo, handleAdminSuccess } from '../../../../../../../common/SharedFunctions';

interface ReferralRow {
    personName?: string;
    businessName?: string;
    firstName?: string;
    lastName?: string;
    startDate?: string;
    iccaDownloadToken?: string;
    generatingIccaPdfUrl?: string;
}

interface AcceptedReferralTableProps {
    searchText: string;
}

export const AcceptedReferralTable: React.FC<AcceptedReferralTableProps> = ({ searchText }) => {
    const [rows, set_rows] = useState([]);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const filteringOperators = new TableFilteringOperators(searchText);
    const [isLoading, set_isLoading] = useState(false);

    const [sort, set_sort] = useState<SortState>({
        column: 'personName',
        direction: 'asc',
    });
    const filtered = filteringOperators.filterRows(rows);
    const mapped: ReferralRow[] = filtered.map((row) => ({
        ...row,
        startDate: formatDate(new Date(row.iccaDate)),
    }));
    const sortedRows = _.orderBy(mapped, sortTable(sort), sort.direction);

    const _loadReferralClients = async () => {
        set_isLoading(true);
        const response = await brokerClientApi.getAcceptedReferralsGridRows();

        set_isLoading(false);

        if (response.statusCode !== 200) return;

        const dataRows = response.result;
        dataRows.forEach((row) => {
            if (row.generatingIccaPdfUrl) {
                const ws = new WebSocket(row.generatingIccaPdfUrl);
                ws.onopen = () => {
                    handleAdminInfo('Generating PDF...');
                };

                ws.onmessage = async (event) => {
                    await _loadReferralClients();
                    handleAdminSuccess('ICCA PDF Reach for download');
                };
            }
        });

        set_rows(dataRows);
    };

    const _refreshGrid = async (e) => {
        e.preventDefault();
        await _loadReferralClients();
    };

    useEffect(() => {
        async function load() {
            await _loadReferralClients();

            refreshEvents.addRefreshListener(_loadReferralClients);
        }

        load();
    }, []);

    const renderReferralRepresentative = (representative: string | undefined) => {
        return <RepresentativeCell representative={representative} />;
    };

    const renderReferralBusinessName = (businessName: string | undefined) => {
        return <BusinessNameCell businessName={businessName} />;
    };

    const renderReferralICCA = (iccaDownloadToken: string | undefined, generatingIccaPdfUrl: string | undefined) => {
        return (
            <ICCACell
                iccaDownloadToken={iccaDownloadToken}
                generatingIccaPdfUrl={generatingIccaPdfUrl}
                onRefreshGrid={_refreshGrid}
            />
        );
    };

    const renderReferralICCAStartDate = (iccaDate: string | undefined) => {
        return <ICCAStartDateCell iccaStart={iccaDate} />;
    };

    const isEmptyResults = sortedRows.length === 0;
    const data: TableRow[] =
        sortedRows.length === 0
            ? [
                  {
                      personName: renderReferralRepresentative(undefined),
                      businessName: renderReferralBusinessName(undefined),
                      icca: renderReferralICCA(undefined, undefined),
                      startDate: renderReferralICCAStartDate(undefined),
                  },
              ]
            : sortedRows.map((referral) => ({
                  personName: renderReferralRepresentative(referral.personName),
                  businessName: renderReferralBusinessName(referral.businessName),
                  icca: renderReferralICCA(referral.iccaDownloadToken, referral.generatingIccaPdfUrl),
                  startDate: renderReferralICCAStartDate(referral.startDate),
              }));

    const cardHeader = () => {
        return (
            <>
                <div>
                    <FontAwesomeIcon
                        icon={faThumbsUp}
                        className='icon'
                        style={{ paddingRight: '10px', opacity: '60%' }}
                    />
                    Accepted Referrals
                </div>
            </>
        );
    };

    return (
        <>
            <Card
                header={cardHeader()}
                headerTheme={CardHeaderTheme.Gray}
                theme={CardTheme.White}
                borderRadius={CardBorderRadius.Small}
                borderStyle={CardBorderStyle.Solid}
                collapsible={true}
            >
                {isLoading ? (
                    <ReferralTableLoadIndicator message='Loading your Accepted Referrals...' />
                ) : !isEmptyResults ? (
                    <Table
                        headerTheme={TableHeaderTheme.Transparent}
                        rowTheme={TableRowTheme.Transparent}
                        borderRadius={TableBorderRadius.None}
                        borderStyle={TableBorderStyle.None}
                        showHeader={true}
                        data={data}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='personName'
                            header='Representative'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='businessName'
                            header='Business'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='icca'
                            header='ICCA'
                            width='20%'
                        />
                        <TableColumn
                            columnKey='startDate'
                            header='ICCA Start'
                            width='20%'
                        />
                    </Table>
                ) : (
                    <EmptyReferrals />
                )}
            </Card>
        </>
    );
};
